import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    RowSelectionState,
    SortingState,
    useReactTable
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { motion } from "framer-motion";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    FaArrowsAltV,
    FaLongArrowAltDown,
    FaLongArrowAltUp
} from "react-icons/fa";
import { AuditObjectTypes, TableColumns } from "../../_core/_model.ts";
import { GetTableColumns, UpsertTableColumn } from "../../_core/_request.ts";
import { ThemeContext } from "../../context/ThemeContext.js";
import TooltipButton from "../Tools/TooltipButton.tsx";

const EMPTY_ARRAY: any[] = [];
const DEFAULT_COLUMN = { size: undefined };

export interface colItem {
    objectFieldID?: string;
    accessorFn?: any;
    accessorKey?: any;
    enableSorting: boolean;
    enableResizing: boolean;
    cell?: any;
    size?: number;
    minSize?: number;
    header?: any;
    sortFn?: any;
    joyClass?: string;
}

interface request {
    objType: AuditObjectTypes;
    columnDefinition: { [key: string]: colItem };
    needRowSelection: boolean;
    rows: any[];
    loading: boolean;
    isSelected: (row: any) => boolean;
    setSelected: (row: any, col: any, indx: number) => void;
    needSelection: boolean;
    onRowSelection?: (rows: any[]) => void;
    emptyMessage: any;
    handleRowDoubleClick: (row: any) => void;
    expandedUI: any;
}

export function TableLayoutEx({
    objType,
    columnDefinition,
    rows,
    loading,
    isSelected,
    setSelected,
    needSelection,
    emptyMessage,
    handleRowDoubleClick,
    expandedUI,
    needRowSelection,
    onRowSelection
}: request) {
    const { themeConfig } = useContext(ThemeContext);
    const parentRef = useRef<HTMLDivElement>(null);

    const [loadingMeta, setLoadingMeta] = useState<boolean>(true);
    const [columns, setColumns] = useState<any[]>([]);
    const [dbColumns, setDBColumns] = useState<TableColumns[]>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 1000 });
    const [columnSizingInfo, setColumnSizingInfo] = useState<any>({});
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    useEffect(() => {
        let isMounted = true;
        if (objType >= 0) {
            setLoadingMeta(true);
            GetTableColumns(objType).then((res) => {
                if (isMounted) {
                    setDBColumns(res.data);
                }
            });
        }
        return () => { isMounted = false; };
    }, [objType]);

    useEffect(() => {
        if (dbColumns.length === 0) return;

        let newCols: any[] = [];
        let sortCol: any[] = [];

        for (const key in columnDefinition) {
            let cCol = columnDefinition[key];
            let tmpColumn: ColumnDef<any> = {
                accessorFn: cCol.accessorFn,
                accessorKey: cCol.accessorKey,
                cell: cCol.cell,
                id: cCol.objectFieldID,
                enableSorting: cCol.enableSorting,
                enableResizing: cCol.enableResizing,
                header: cCol.header,
                meta: { joyClass: cCol.joyClass },
                ...(cCol.sortFn && { sortingFn: cCol.sortFn })
            };

            let tIndx = dbColumns.findIndex((x) => x.field?.toLowerCase() === key.toLowerCase());
            if (tIndx === -1) {
                tIndx = dbColumns.findIndex((x) => x.fieldDisplayName?.toLowerCase() === key.toLowerCase());
            }

            if (tIndx > -1) {
                const matchedDbCol = dbColumns[tIndx];
                if (matchedDbCol.sortColumn) {
                    sortCol = [{ id: matchedDbCol.objectFieldID.toString(), desc: (matchedDbCol.sortColumn === "desc") }];
                }
                tmpColumn.size = matchedDbCol.width;
                tmpColumn.header = (typeof tmpColumn.header === 'undefined' || typeof tmpColumn.header === 'string')
                    ? matchedDbCol.fieldDisplayName
                    : tmpColumn.header;
                tmpColumn.id = matchedDbCol.objectFieldID.toString();
                tmpColumn.minSize = cCol.minSize ?? 35;
            } else {
                tmpColumn.header = cCol.header;
                tmpColumn.size = cCol.size;
            }
            newCols.push(tmpColumn);
        }

        setSorting(sortCol);
        setColumns(newCols);
        setLoadingMeta(false);
    }, [columnDefinition, dbColumns]);

    const colSortingChanged = useCallback((sortUpdater: any) => {
        setSorting(sortUpdater);
        const newSort = typeof sortUpdater === 'function' ? sortUpdater(sorting) : sortUpdater;

        if (!newSort || newSort.length === 0) return;

        let sortVal = newSort[0];
        let prev = [...dbColumns];

        let nIndx = prev.findIndex((x) => x.objectFieldID.toString() === sortVal.id);
        if (nIndx === -1) nIndx = prev.findIndex((x) => x.field.toLowerCase() === sortVal.id.toLowerCase());

        if (nIndx > -1) {
            const updatedLocal = prev.map(x => ({ ...x, sortColumn: "" }));
            updatedLocal[nIndx].sortColumn = sortVal.desc ? "desc" : "asc";

            // FIX: Cast to 'any' to bypass 'TableColumns[]' strict type check for width/createdAt
            const sanitizedRequest: any[] = updatedLocal.map(col => ({
                ...col,
                createdAt: null,
                width: col.width?.toString() ?? "0"
            }));

            UpsertTableColumn(sanitizedRequest as any).then(() => {
                setDBColumns(updatedLocal);
            });
        }
    }, [dbColumns, sorting]);

    const setColumnSize = useCallback((colFunc: any) => {
        let tColSize = colFunc();
        let fname = Object.keys(tColSize)[0];
        let prev = [...dbColumns];
        let nIndx = prev.findIndex(((x) => x.objectFieldID.toString() == fname));

        if (nIndx > -1) {
            let colIndx = columns.findIndex((x) => x.id == fname);
            if (colIndx > -1) {
                let colPrev = [...columns];
                colPrev[colIndx].size = tColSize[fname];
                setColumns(colPrev);
            }
        }
    }, [columns, dbColumns]);

    const table = useReactTable({
        data: rows ?? EMPTY_ARRAY,
        columns,
        defaultColumn: DEFAULT_COLUMN,
        state: {
            sorting,
            pagination,
            columnSizingInfo,
            rowSelection
        },
        enableRowSelection: needRowSelection,
        onRowSelectionChange: setRowSelection,
        columnResizeMode: "onChange",
        onSortingChange: colSortingChanged,
        onColumnSizingChange: setColumnSize,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onColumnSizingInfoChange: (updater) => {
            const newInfo = updater instanceof Function ? updater(columnSizingInfo) : updater;
            setColumnSizingInfo(newInfo);

            if (!newInfo.isResizingColumn && columnSizingInfo.isResizingColumn) {
                let prev = [...dbColumns];
                let nIndx = prev.findIndex(((x) => x.objectFieldID.toString() == columnSizingInfo.isResizingColumn));
                if (nIndx === -1) return;

                let pDBColumn = { ...prev[nIndx] };
                const newWidth = columnSizingInfo.startSize + columnSizingInfo.deltaOffset;

                pDBColumn.width = newWidth;
                // FIX: Cast to 'any' to allow string width and null createdAt
                let req = { ...pDBColumn, createdAt: null, width: pDBColumn.width.toString() };

                UpsertTableColumn([req] as any).then(() => {
                    prev[nIndx] = pDBColumn;
                    setDBColumns(prev);
                })
            }
        },
    });

    const { rows: tableRows } = table.getRowModel();
    const rowVirtualizer = useVirtualizer({
        count: tableRows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 48,
        overscan: 15,
    });

    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();
    const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

    useEffect(() => {
        if (typeof onRowSelection != 'undefined') {
            const selectedOriginals = table.getSelectedRowModel().rows.map(r => r.original);
            onRowSelection(selectedOriginals);
        }
    }, [rowSelection, table, onRowSelection]);

    const getSortIcon = (sort: string | false) => {
        if (sort === 'asc') return <FaLongArrowAltUp />;
        if (sort === 'desc') return <FaLongArrowAltDown />;
        return <FaArrowsAltV />;
    };

    const allColumns = table.getAllLeafColumns();
    const flexibleColumns = allColumns.filter(col => typeof col.columnDef.size !== 'number');
    const totalDefinedWidth = allColumns.filter(col => typeof col.columnDef.size === 'number').reduce((sum, col) => sum + (col.getSize?.() || 0), 0);
    const remainingWidth = Math.max(1000 - totalDefinedWidth, 0);
    const defaultFlexibleWidth = flexibleColumns.length > 0 ? Math.floor(remainingWidth / flexibleColumns.length) : 0;

    return (
        loadingMeta ?
            <div className="d-flex align-items-center gap-2 w-100 justify-content-center">
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead className="table-thead">
                        <tr>
                            {Object.keys(columnDefinition).map((k, index) => (
                                <th key={index} className={'text-color table-th'} style={{ fontWeight: "normal", fontSize: "12px", padding: index > 0 ? '12px 4px' : '12px 4px 12px 12px', textAlign: 'left', verticalAlign: 'middle', position: 'relative' }}>
                                    <div className="w-100 h-100 d-flex flex-row align-items-center justify-content-between">
                                        {typeof columnDefinition[k].header == 'function' ? columnDefinition[k].header({ table }) : columnDefinition[k].header}
                                    </div>
                                </th>
                            ))}
                            <th style={{ width: "auto" }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {[...Array(5)].map((_, i) => (
                            <tr key={i}>
                                {Object.keys(columnDefinition).map((col, j) => (
                                    <td key={j} style={{ padding: j > 0 ? '12px 4px' : '12px 4px 12px 12px' }}>
                                        <div className="placeholder-glow"><span className="placeholder col-8"></span></div>
                                    </td>
                                ))}
                                <td style={{ width: "auto" }}></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div> :
            <div
                ref={parentRef}
                className="d-flex flex-column px-4"
                style={{ flex: 1, overflow: 'auto', minHeight: 0, maxHeight: "100%", maxWidth: "100%" }}
            >
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: "fixed" }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 11 }} className="table-thead">
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id} className='bg-theme-card'>
                                {headerGroup.headers.map((header, index) => {
                                    const headerText = String(typeof header.column.columnDef.header === "string" ? header.column.columnDef.header : header.column.columnDef.header?.toString?.() || "");
                                    const shortWord = headerText.split(" ")[0]?.slice(0, 3).toLowerCase().replace(/[^a-z0-9]/g, '') || `col${index + 1}`;
                                    const dynamicJoyClass = `j_tbl${index + 1}_${shortWord}`;

                                    return (
                                        <th
                                            key={header.id}
                                            className={`${header.column.getCanSort() ? 'text-color can-sort-th' : 'text-color table-th'} ${dynamicJoyClass} `}
                                            style={{
                                                fontWeight: "normal",
                                                fontSize: "12px",
                                                padding: index > 0 ? '12px 4px' : '12px 4px 12px 12px',
                                                textAlign: 'left',
                                                verticalAlign: 'middle',
                                                position: 'relative',
                                                userSelect: 'none',
                                                ...(header.column.getSize() ? { width: header.column.getSize() } : { width: defaultFlexibleWidth }),
                                                minWidth: header.column.columnDef.minSize ?? 80
                                            }}
                                        >
                                            <div
                                                className="w-100 h-100 d-flex flex-row align-items-center justify-content-between"
                                                {...{
                                                    onClick: header.column.getToggleSortingHandler?.(),
                                                    style: { cursor: header.column.getCanSort() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '6px' }
                                                }}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <span className={header.column.getIsSorted() ? "table-sorticon active" : "table-sorticon"}>
                                                    {header.column.getCanSort() && getSortIcon(header.column.getIsSorted())}
                                                </span>
                                            </div>

                                            {header.column.getCanResize() && (
                                                <div
                                                    onMouseDown={header.getResizeHandler()}
                                                    onTouchStart={header.getResizeHandler()}
                                                    style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '2px', backgroundColor: 'rgba(0,0,0,0.1)', cursor: 'col-resize', zIndex: 10, touchAction: 'none' }}
                                                    className="resizer table-resizer"
                                                />
                                            )}
                                        </th>)
                                })}
                                <th style={{ width: "auto" }}></th>
                            </tr>
                        ))}
                    </thead>
                    <tbody className="table-tbody">
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <tr key={i}>
                                    {table.getAllColumns().map((col, j) => (
                                        <td key={j} style={{ padding: j > 0 ? '12px 4px' : '12px 4px 12px 12px' }}>
                                            <div className="placeholder-glow"><span className="placeholder col-8"></span></div>
                                        </td>
                                    ))}
                                    <td style={{ width: "auto" }}></td>
                                </tr>
                            ))
                        ) :
                            tableRows.length === 0 ? (
                                <tr>
                                    <td colSpan={table.getAllColumns().length + 1}>{emptyMessage}</td>
                                </tr>
                            ) : (
                                <>
                                    {paddingTop > 0 && <tr><td style={{ height: `${paddingTop}px` }} colSpan={columns.length + 1} /></tr>}
                                    {virtualRows.map((virtualRow) => {
                                        const row = tableRows[virtualRow.index];
                                        return (
                                            <React.Fragment key={row.id}>
                                                <motion.tr
                                                    layout
                                                    onDoubleClick={() => handleRowDoubleClick(row)}
                                                    transition={{ duration: 0.2 }}
                                                    className={isSelected(row) ? "selected" : ""}
                                                    style={{ cursor: needSelection ? "pointer" : "default", fontWeight: isSelected(row) ? "bold" : "normal" }}
                                                >
                                                    {row.getVisibleCells().map((cell, indx) => (
                                                        <td
                                                            key={cell.id}
                                                            onClick={() => setSelected(row, cell, indx)}
                                                            style={{
                                                                padding: indx > 0 ? '12px 4px' : '12px 4px 12px 12px',
                                                                color: themeConfig.colors.text,
                                                                verticalAlign: 'middle',
                                                                minWidth: cell.column.columnDef.minSize ?? 80,
                                                                width: cell.column.getSize(),
                                                            }}
                                                            className={cell.column.id === 'action' ? "" : "overflow-hidden text-truncate"}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                {typeof cell.column.columnDef.size === 'undefined' ? (
                                                                    <TooltipButton title={cell.getValue() as string}>
                                                                        <span style={{ maxWidth: "350px" }} className="overflow-hidden d-inline-flex text-truncate">
                                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                        </span>
                                                                    </TooltipButton>
                                                                ) : (
                                                                    <span style={{
                                                                        maxWidth: cell.column.id === 'action' ? 'none' : `${cell.column.getSize()}px`,
                                                                        minWidth: `${cell.column.columnDef.minSize ?? 80}px`,
                                                                    }} className={cell.column.id === 'action' ? "d-inline-flex" : "overflow-hidden d-inline-flex text-truncate"}>
                                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    ))}
                                                    <td style={{ width: "auto" }}></td>
                                                </motion.tr>
                                                {row.getIsExpanded() && (
                                                    <tr>
                                                        <td colSpan={row.getVisibleCells().length + 1}>{expandedUI(row)}</td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {paddingBottom > 0 && <tr><td style={{ height: `${paddingBottom}px` }} colSpan={columns.length + 1} /></tr>}
                                </>
                            )}
                    </tbody>
                </table>
            </div>
    );
}

export default TableLayoutEx;